import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  User,
  Home,
  Smartphone,
  Menu,
  X,
  MapPin,
  Tag,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export const NavbarGeneral: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDispositivosOpen, setIsDispositivosOpen] = useState(false);

  const { usuario, logout } = useAuth();

  const handleLogout = () => {
    logout();
  };

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = () => {
      setIsProfileOpen(false);
      setIsDispositivosOpen(false);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const styles = `
    .samsung-navbar {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: rgba(26, 26, 26, 0.98);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 2px 20px rgba(0, 0, 0, 0.5);
      position: sticky;
      top: 0;
      z-index: 1000;
    }

    .navbar-container {
      max-width: 1400px;
      margin: 0 auto;
      padding: 0 2rem;
    }

    .navbar-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 72px;
      width: 100%;
    }

    .logo-container {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .samsung-logo {
      display: flex;
      align-items: center;
      text-decoration: none;
      transition: all 0.3s ease;
      padding: 8px 0;
    }

    .samsung-logo:hover .logo-text {
      text-shadow: 0 0 20px rgba(255, 255, 255, 0.6);
    }

    .logo-text {
      color: #ffffff;
      font-size: 24px;
      font-weight: 300;
      letter-spacing: 6px;
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
      transition: all 0.3s ease;
    }

    .desktop-menu {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .nav-link {
      display: flex;
      align-items: center;
      gap: 8px;
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 400;
      letter-spacing: 0.3px;
      transition: all 0.3s ease;
      position: relative;
    }

    .nav-link::after {
      content: '';
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%) scaleX(0);
      width: calc(100% - 40px);
      height: 1px;
      background: #ffffff;
      transition: transform 0.3s ease;
    }

    .nav-link:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.05);
    }

    .nav-link:hover::after {
      transform: translateX(-50%) scaleX(1);
    }

    .nav-button {
      display: flex;
      align-items: center;
      gap: 8px;
      color: rgba(255, 255, 255, 0.85);
      background: transparent;
      border: none;
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 15px;
      font-weight: 400;
      letter-spacing: 0.3px;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;
    }

    .nav-button::after {
      content: '';
      position: absolute;
      bottom: 8px;
      left: 50%;
      transform: translateX(-50%) scaleX(0);
      width: calc(100% - 40px);
      height: 1px;
      background: #ffffff;
      transition: transform 0.3s ease;
    }

    .nav-button:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.05);
    }

    .nav-button:hover::after {
      transform: translateX(-50%) scaleX(1);
    }

    .dropdown-menu {
      position: absolute;
      top: calc(100% + 12px);
      right: 0;
      min-width: 240px;
      background: rgba(20, 20, 20, 0.98);
      backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
      padding: 8px;
      z-index: 1001;
      animation: dropdownSlide 0.3s ease-out;
    }

    @keyframes dropdownSlide {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .dropdown-item {
      display: block;
      width: 100%;
      padding: 12px 16px;
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 400;
      transition: all 0.2s ease;
    }

    .dropdown-item:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.08);
      transform: translateX(2px);
    }

    .dropdown-button {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 12px 16px;
      color: rgba(255, 255, 255, 0.85);
      background: transparent;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 400;
      text-align: left;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .dropdown-button:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.08);
      transform: translateX(2px);
    }

    .user-info {
      padding: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      margin-bottom: 8px;
    }

    .user-name {
      color: #ffffff;
      font-weight: 500;
      font-size: 15px;
      margin-bottom: 4px;
    }

    .user-email {
      color: rgba(255, 255, 255, 0.6);
      font-size: 13px;
    }

    .mobile-menu-button {
      display: none;
      color: rgba(255, 255, 255, 0.9);
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.2);
      padding: 10px;
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .mobile-menu-button:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.3);
    }

    .mobile-menu {
      position: fixed;
      top: 72px;
      left: 0;
      right: 0;
      background: rgba(20, 20, 20, 0.98);
      backdrop-filter: blur(20px);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      padding: 20px;
      animation: mobileSlide 0.3s ease-out;
      max-height: calc(100vh - 72px);
      overflow-y: auto;
    }

    @keyframes mobileSlide {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .mobile-nav-link {
      display: flex;
      align-items: center;
      gap: 12px;
      color: rgba(255, 255, 255, 0.9);
      text-decoration: none;
      padding: 16px 20px;
      border-radius: 10px;
      font-size: 16px;
      font-weight: 400;
      margin-bottom: 8px;
      transition: all 0.3s ease;
    }

    .mobile-nav-link:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.08);
      transform: translateX(4px);
    }

    .mobile-user-section {
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      padding-top: 20px;
      margin-top: 20px;
    }

    .mobile-user-info {
      padding: 16px 20px;
      background: rgba(255, 255, 255, 0.05);
      border-radius: 10px;
      margin-bottom: 16px;
    }

    .mobile-logout-button {
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      color: rgba(255, 255, 255, 0.9);
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.15);
      padding: 16px 20px;
      border-radius: 10px;
      font-size: 16px;
      font-weight: 400;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .mobile-logout-button:hover {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.25);
      transform: translateX(4px);
    }

    @media (max-width: 768px) {
      .desktop-menu {
        display: none;
      }
      
      .mobile-menu-button {
        display: block;
      }

      .navbar-container {
        padding: 0 1rem;
      }

      .logo-text {
        font-size: 20px;
        letter-spacing: 4px;
      }

      .navbar-content {
        height: 64px;
      }

      .mobile-menu {
        top: 64px;
        max-height: calc(100vh - 64px);
      }
    }

    @media (min-width: 769px) {
      .mobile-menu {
        display: none !important;
      }
    }

    /* Custom scrollbar for mobile menu */
    .mobile-menu::-webkit-scrollbar {
      width: 4px;
    }

    .mobile-menu::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
    }

    .mobile-menu::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
      border-radius: 2px;
    }

    .mobile-menu::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <nav className="samsung-navbar">
        <div className="navbar-container">
          <div className="navbar-content">
            {/* Logo */}
            <div className="logo-container">
              <Link to="/welcome" className="samsung-logo">
                <span className="logo-text">SAMSUNG</span>
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="desktop-menu">
              <Link to="/welcome" className="nav-link">
                <Home size={18} />
                Inicio
              </Link>

              {/* Dispositivos Dropdown */}
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setIsDispositivosOpen(!isDispositivosOpen)}
                  className="nav-button"
                >
                  <Smartphone size={18} />
                  Dispositivos
                  <ChevronDown size={16} />
                </button>

                {isDispositivosOpen && (
                  <div className="dropdown-menu">
                    <Link
                      to="/dispositivos"
                      className="dropdown-item"
                      onClick={() => setIsDispositivosOpen(false)}
                    >
                      Lista de Dispositivos
                    </Link>
                    <Link
                      to="/dashboard"
                      className="dropdown-item"
                      onClick={() => setIsDispositivosOpen(false)}
                    >
                      Dashboard
                    </Link>
                  </div>
                )}
              </div>

              {/* Sentinels */}
              <Link to="/sentinels" className="nav-link">
                <Tag size={18} />
                Tags
              </Link>

              {/* Mapa */}
              <Link to="/mapa" className="nav-link">
                <MapPin size={18} />
                Mapa
              </Link>

              {/* Profile Dropdown */}
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="nav-button"
                >
                  <User size={18} />
                  {usuario?.nombre}
                  <ChevronDown size={16} />
                </button>

                {isProfileOpen && (
                  <div className="dropdown-menu">
                    <div className="user-info">
                      <div className="user-name">
                        {usuario?.nombre} {usuario?.apellido_paterno}
                      </div>
                      <div className="user-email">{usuario?.correo}</div>
                    </div>
                    <button onClick={handleLogout} className="dropdown-button">
                      <LogOut size={16} />
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-menu-button"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="mobile-menu">
              <Link
                to="/welcome"
                className="mobile-nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <Home size={20} />
                Inicio
              </Link>
              <Link
                to="/dispositivos"
                className="mobile-nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <Smartphone size={20} />
                Dispositivos
              </Link>
              <Link
                to="/sentinels"
                className="mobile-nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <Tag size={20} />
                Sentinels
              </Link>
              <Link
                to="/dashboard"
                className="mobile-nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <User size={20} />
                Dashboard
              </Link>
              <Link
                to="/mapa"
                className="mobile-nav-link"
                onClick={() => setIsMenuOpen(false)}
              >
                <MapPin size={20} />
                Mapa
              </Link>

              <div className="mobile-user-section">
                <div className="mobile-user-info">
                  <div className="user-name">
                    {usuario?.nombre} {usuario?.apellido_paterno}
                  </div>
                  <div className="user-email">{usuario?.correo}</div>
                </div>
                <button onClick={handleLogout} className="mobile-logout-button">
                  <LogOut size={20} />
                  Cerrar Sesión
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
